import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { PlayerRole, type PensieveQuestionLevel, getLanguage } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';
import { getRoleConfig } from '../../utils/roleConfig';
import { usePersistedPensieveLevelRange } from '../../hooks/usePersistedPensieveLevelRange';

const LEVELS: PensieveQuestionLevel[] = [0, 1, 2, 3];

export const UndercoverRoleReveal: React.FC = () => {
  const { socket, gameState, currentRoom, myPlayerId } = useSocket();
  const t = useTranslations();
  const { range, setMin, setMax } = usePersistedPensieveLevelRange();
  const isZh = getLanguage() === 'zh';

  if (!gameState || !currentRoom) {
    return null;
  }

  const roleConfig = getRoleConfig(t);
  const isHost = currentRoom.hostId === myPlayerId;
  const gameResult = gameState.gameResult;
  const eliminatedPlayers = gameState.eliminatedPlayers || [];
  const isGhostMode = currentRoom.undercoverConfig?.mode === 'ghost';
  const bonus = gameState.undercoverPensieveBonus;
  const drawerPlayer = bonus ? currentRoom.players.find((p) => p.id === bonus.drawerId) : null;
  const isDrawer = Boolean(myPlayerId && bonus && bonus.drawerId === myPlayerId);
  const bonusQuestionText = bonus?.question
    ? isZh
      ? bonus.question.questionZh
      : bonus.question.question
    : '';

  const handleBonusDraw = () => {
    if (!socket || !isDrawer || !bonus) return;
    socket.emit('client:undercover-pensieve-draw', range);
  };

  // Determine result message
  let resultText = '';
  let resultColor = '';

  if (gameResult === 'civilian_win') {
    resultText = isGhostMode ? t.games.undercover.ghostResultCivilianWinReveal : t.games.undercover.civilianWin;
    resultColor = 'from-green-600 to-emerald-600';
  } else if (gameResult === 'undercover_win') {
    resultText = isGhostMode
      ? gameState.ghostTie
        ? t.games.undercover.ghostResultUndercoverWinTie
        : t.games.undercover.ghostResultUndercoverWinSurvived
      : t.games.undercover.undercoverWin;
    resultColor = 'from-red-600 to-rose-600';
  } else if (gameResult === 'blank_win') {
    resultText = t.games.undercover.blankWin;
    resultColor = 'from-purple-600 to-violet-600';
  } else if (gameResult === 'tie') {
    resultText = t.games.undercover.tie;
    resultColor = 'from-gray-600 to-slate-600';
  }

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
      <div className="w-full text-center mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-2 px-2">
          {t.games.undercover.results}
        </h2>
        {resultText && (
          <h3 className={`text-3xl sm:text-4xl font-bold bg-gradient-to-r ${resultColor} bg-clip-text text-transparent mb-2 mt-4`}>
            {resultText}
          </h3>
        )}
        {!isGhostMode && gameState.civilianWord && gameState.undercoverWord && (
          <div className="w-full mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">{t.games.undercover.civilianWord}:</p>
            <div className="w-full overflow-x-auto">
              <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-gray-800 whitespace-nowrap">{gameState.civilianWordZh || gameState.civilianWord}</p>
            </div>
            <p className="text-sm text-gray-600 mt-2 mb-1">{t.games.undercover.undercoverWord}:</p>
            <div className="w-full overflow-x-auto">
              <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-gray-800 whitespace-nowrap">{gameState.undercoverWordZh || gameState.undercoverWord}</p>
            </div>
          </div>
        )}
      </div>

      <div className="w-full space-y-3 sm:space-y-4 mb-4 sm:mb-6">
        {currentRoom.players
          .filter(p => p.role !== PlayerRole.WORDSMITH)
          .map((player) => {
            if (!player.role) return null;
            const config = roleConfig[player.role];
            const isEliminated = eliminatedPlayers.includes(player.id);
            const isMyPlayer = player.id === myPlayerId;

            return (
              <div
                key={player.id}
                className={`p-4 sm:p-6 rounded-xl border-2 bg-gradient-to-r ${config.gradient} text-white ${
                  isEliminated ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 sm:gap-4">
                    <span className="text-3xl sm:text-4xl">{config.emoji}</span>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold">{player.name}</h3>
                      <p className="text-base sm:text-lg opacity-90">{config.name}</p>
                      {isEliminated && (
                        <p className="text-sm mt-1 opacity-75">{t.games.undercover.eliminated}</p>
                      )}
                      {isMyPlayer && (
                        <p className="text-sm mt-1 opacity-90">{t.games.undercover.you}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {bonus && (
        <div className="w-full bg-purple-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-purple-300">
          <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 flex items-center gap-2 text-purple-800">
            <span className="text-lg sm:text-xl">🔮</span>
            {t.games.deadpan.punishmentTitle}
          </h3>
          <p className="text-sm sm:text-base text-purple-900 mb-3">
            {bonus.mode === 'others'
              ? t.games.undercover.pensieveBonusDescriptionSpyWins
              : t.games.undercover.pensieveBonusDescriptionSpyLoses}
          </p>
          <p className="text-sm sm:text-base text-purple-900 mb-4">
            <span className="font-semibold">{t.games.undercover.pensieveBonusDrawerLabel}:</span>{' '}
            {drawerPlayer?.name ?? '—'}
          </p>

          {isDrawer && (
            <div className="rounded-lg border border-purple-200 bg-white p-3 sm:p-4 mb-3">
              <p className="text-sm font-semibold text-purple-800 mb-3">{t.games.undercover.pensieveBonusYouAreDrawer}</p>
              <div className="flex flex-col gap-3 mb-2">
                <span className="text-sm font-medium text-gray-700">{t.games.deadpan.punishmentChooseLevelLabel}</span>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <label htmlFor="undercover-pensieve-level-min" className="text-sm font-medium text-gray-700">
                      {t.games.pensieve.levelMinLabel}
                    </label>
                    <select
                      id="undercover-pensieve-level-min"
                      value={range.min}
                      onChange={(e) => setMin(Number(e.target.value) as PensieveQuestionLevel)}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 min-w-[4rem]"
                    >
                      {LEVELS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <label htmlFor="undercover-pensieve-level-max" className="text-sm font-medium text-gray-700">
                      {t.games.pensieve.levelMaxLabel}
                    </label>
                    <select
                      id="undercover-pensieve-level-max"
                      value={range.max}
                      onChange={(e) => setMax(Number(e.target.value) as PensieveQuestionLevel)}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 min-w-[4rem]"
                    >
                      {LEVELS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleBonusDraw}
                  className="w-full min-h-[2.75rem] px-6 py-3 text-base bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700"
                >
                  {t.games.deadpan.punishmentDrawButton}
                </button>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-purple-200 bg-white p-3 sm:p-4 mb-3">
            <p className="text-sm font-semibold text-purple-800 mb-2">{t.games.deadpan.punishmentQuestionLabel}</p>
            {bonus.question ? (
              <>
                <p className="text-sm text-purple-600 mb-1">
                  {t.games.pensieve.levelLabel}: {bonus.question.level}
                </p>
                <p className="text-base sm:text-lg font-medium text-purple-900 break-words">{bonusQuestionText}</p>
                <p className="text-sm text-purple-800 mt-3 font-medium">
                  {bonus.mode === 'others'
                    ? t.games.undercover.pensieveBonusEveryoneElseAnswers
                    : t.games.undercover.pensieveBonusSelfAnswerHint}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-600">
                {isDrawer ? t.games.deadpan.punishmentNoQuestionYet : t.games.undercover.pensieveBonusWaitingDrawer}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="w-full mt-4 sm:mt-6 pt-4 sm:pt-6 border-t-2 border-gray-200">
        {isHost ? (
          <button
            onClick={() => socket?.emit('client:start-new-game')}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            🎮 {t.ui.startNewGame}
          </button>
        ) : (
          <p className="text-center text-gray-500 text-xs sm:text-sm">{t.games.deadpan.waitingForHostToStartNewGame}</p>
        )}
      </div>
    </div>
  );
};
